import { useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Mail, Phone, MapPin, MessageCircle, Upload, Send } from "lucide-react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { CONTACT_EMAIL, CITY, OWNER, WHATSAPP_LINK } from "@/lib/constants";
import { useSEO } from "@/lib/useSEO";

export default function Contact() {
  useSEO({
    title: "Contact ScholarPrintHub — Thesis Printing & Academic Services in Lucknow",
    description: "Get in touch with Suraj Dubey at ScholarPrintHub. Email surajdubeyks@gmail.com or WhatsApp +91 87954 47361. Lucknow, India — serving scholars across India.",
    keywords: "contact scholarprinthub, thesis printing lucknow contact, thesis writing service india contact, surajdubeyks",
  });
  const [form, setForm] = useState({ name: "", mobile: "", email: "", service: "", message: "" });
  const [sending, setSending] = useState(false);
  const set = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/contact", form);
      toast.success("Message sent. We'll reply within 12 hours.");
      setForm({ name: "", mobile: "", email: "", service: "", message: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send");
    } finally { setSending(false); }
  };

  return (
    <div data-testid="contact-page" className="container-app py-16 grid lg:grid-cols-12 gap-12">
      <div className="lg:col-span-5">
        <span className="section-eyebrow">Contact</span>
        <div className="divider-gold mt-2 mb-4" />
        <h1 className="font-display text-4xl sm:text-5xl text-navy">Let&apos;s talk about your project.</h1>
        <p className="mt-4 text-slate-600 text-sm">Send us a message, or reach out directly. We usually reply within a few hours.</p>

        <div className="mt-8 space-y-4 text-sm">
          <div className="flex items-center gap-3"><span className="w-9 h-9 border border-slate-200 rounded-sm flex items-center justify-center"><Mail className="w-4 h-4 text-royal" /></span><a href={`mailto:${CONTACT_EMAIL}`} className="text-navy hover:text-royal">{CONTACT_EMAIL}</a></div>
          <div className="flex items-center gap-3"><span className="w-9 h-9 border border-slate-200 rounded-sm flex items-center justify-center"><MapPin className="w-4 h-4 text-royal" /></span><span>{CITY}</span></div>
          <div className="flex items-center gap-3"><span className="w-9 h-9 border border-slate-200 rounded-sm flex items-center justify-center"><Phone className="w-4 h-4 text-royal" /></span><a href={WHATSAPP_LINK} target="_blank" rel="noreferrer" className="text-navy hover:text-royal">WhatsApp us</a></div>
          <div className="border-t border-slate-200 pt-4 mt-6">
            <div className="section-eyebrow">Handled by</div>
            <div className="font-display text-2xl mt-1 text-navy">{OWNER}</div>
            <div className="text-xs text-slate-500">@surajdubeyks</div>
          </div>
        </div>

        <div className="mt-8 flex flex-wrap gap-2">
          <a href={`mailto:${CONTACT_EMAIL}`} className="btn-outline"><Mail className="w-4 h-4" /> Email Us</a>
          <a href={WHATSAPP_LINK} target="_blank" rel="noreferrer" className="btn-gold"><MessageCircle className="w-4 h-4" /> WhatsApp</a>
          <a href="/upload" className="btn-outline"><Upload className="w-4 h-4" /> Upload Document</a>
          <a href="/academic-request" className="btn-primary">Request Quote</a>
        </div>
      </div>

      <form onSubmit={submit} className="lg:col-span-7 border border-slate-200 rounded-sm p-8 bg-white">
        <div className="grid sm:grid-cols-2 gap-4">
          <div><Label className="text-xs">Name</Label><Input required value={form.name} onChange={set("name")} data-testid="contact-name" /></div>
          <div><Label className="text-xs">Mobile number</Label><Input required value={form.mobile} onChange={set("mobile")} data-testid="contact-mobile" /></div>
          <div className="sm:col-span-2"><Label className="text-xs">Email</Label><Input type="email" required value={form.email} onChange={set("email")} data-testid="contact-email" /></div>
          <div className="sm:col-span-2"><Label className="text-xs">Service required</Label><Input value={form.service} onChange={set("service")} placeholder="e.g., Ph.D. thesis printing and gold embossing" data-testid="contact-service" /></div>
          <div className="sm:col-span-2"><Label className="text-xs">Message</Label><Textarea rows={5} required value={form.message} onChange={set("message")} data-testid="contact-message" /></div>
        </div>
        <button type="submit" disabled={sending} data-testid="contact-submit" className="btn-primary mt-6">
          <Send className="w-4 h-4" /> {sending ? "Sending…" : "Send message"}
        </button>
      </form>
    </div>
  );
}
